import { z } from 'zod';
/**
 * Schemas for the Media Calendar (Buffer-backed) endpoints. Mirrors the
 * shape of `CreateMediaPostRequest` / `UpdateMediaPostRequest` in
 * `types/media-calendar.ts`.
 *
 * Limits:
 *   - text capped at 4000 chars (LinkedIn allows 3000, Threads 500, Twitter
 *     280 — we let the platform enforce the small ones and just guard
 *     against absurd payloads server-side).
 *   - up to 10 image URLs (Buffer's hardest cap is 10 for some platforms).
 *   - 20 profile_ids per request — covers any realistic multi-channel post.
 */
const ImageUrl = z
    .string()
    .max(2000)
    .refine((s) => /^https?:\/\//i.test(s), 'Must be an http(s) URL');
export const MediaPostTypeSchema = z.enum(['post', 'story', 'reel']);
export const CreateMediaPostRequestSchema = z
    .object({
    text: z.string().min(1).max(4000),
    profileIds: z.array(z.string().min(1).max(64)).min(1).max(20),
    scheduledAt: z.string().datetime({ offset: true }).optional(),
    imageUrls: z.array(ImageUrl).max(10).optional(),
    now: z.boolean().optional(),
    postType: MediaPostTypeSchema.optional(),
})
    .refine((d) => !(d.now && d.scheduledAt), {
    message: 'Cannot set both `now` and `scheduledAt`',
});
const BufferServiceSchema = z.enum([
    'twitter',
    'facebook',
    'instagram',
    'linkedin',
    'pinterest',
    'tiktok',
    'youtube',
    'threads',
    'mastodon',
    'bluesky',
    'googlebusiness',
    'other',
]);
export const UpdateMediaPostRequestSchema = z
    .object({
    text: z.string().min(1).max(4000).optional(),
    scheduledAt: z.string().datetime({ offset: true }).optional(),
    imageUrls: z.array(ImageUrl).max(10).optional(),
    now: z.boolean().optional(),
    profileService: BufferServiceSchema.optional(),
    postType: MediaPostTypeSchema.optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
export const MediaPostIdParamSchema = z.object({
    id: z.string().min(1).max(64),
});
export const MediaPostsQuerySchema = z.object({
    /** ISO timestamp lower bound (inclusive). */
    from: z.string().datetime({ offset: true }).optional(),
    /** ISO timestamp upper bound (inclusive). */
    to: z.string().datetime({ offset: true }).optional(),
    profileId: z.string().min(1).max(64).optional(),
    /** Include `sent` posts in addition to pending/queued (default true). */
    includeSent: z
        .union([z.literal('true'), z.literal('false')])
        .optional()
        .transform((v) => v !== 'false'),
});
export const GrantMediaCalendarUserBodySchema = z.object({
    userId: z.string().uuid(),
});
//# sourceMappingURL=media-calendar.schema.js.map